import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white border border-[#e5e5e5] text-[#121212] px-4 py-3 text-sm focus:border-[#121212] focus:outline-none rounded-none";
const labelClass = "block text-xs font-mono uppercase tracking-[.14em] text-[#555555] mb-2";

const AdminTalentPage = () => {
  const [profiles, setProfiles] = useState(null);

  const load = () =>
    axios.get(`${API}/talent`).then((res) => setProfiles(res.data));

  useEffect(() => {
    load();
  }, []);

  const update = (slug, key) => (e) =>
    setProfiles(profiles.map((p) => (p.slug === slug ? { ...p, [key]: e.target.value } : p)));

  const updateExpertise = (slug, value) =>
    setProfiles(profiles.map((p) => (p.slug === slug ? { ...p, expertise: value.split("\n").map((s) => s.trim()).filter(Boolean) } : p)));

  async function save(profile) {
    try {
      await axios.put(
        `${API}/admin/talent/${profile.slug}`,
        {
          slug: profile.slug,
          name: profile.name,
          role: profile.role,
          focus: profile.focus,
          expertise: profile.expertise || [],
          img: profile.img,
          caption: profile.caption,
          order: profile.order || 1,
        },
        { withCredentials: true }
      );
      toast.success(`${profile.name} saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the profile.");
    }
  }

  if (profiles === null) {
    return (
      <p className="text-xs font-mono uppercase tracking-[.14em] text-[#555555]">Loading…</p>
    );
  }

  return (
    <div data-testid="admin-talent-page">
      <h1 className="font-disp text-3xl text-[#121212] mb-2">Talent Profiles</h1>
      <p className="text-sm text-[#555555] mb-10">
        Update photos, bios, and areas of expertise for the roster.
      </p>

      <div className="space-y-8">
        {profiles.map((p) => (
          <div key={p.slug} data-testid={`admin-talent-card-${p.slug}`} className="bg-white border border-[#e5e5e5] p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-disp text-2xl text-[#121212]">{p.name}</h2>
              <button
                type="button"
                onClick={() => save(p)}
                data-testid={`talent-save-${p.slug}`}
                className="px-5 py-3 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] transition-colors rounded-none"
              >
                Save Profile
              </button>
            </div>

            {p.img && (
              <figure className="relative aspect-[4/3] bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden max-w-md">
                <img src={p.img} alt={p.caption || p.name} className="absolute inset-0 w-full h-full object-cover" />
              </figure>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Name</label>
                <input type="text" value={p.name} onChange={update(p.slug, "name")} className={inputClass} data-testid={`talent-name-${p.slug}`} />
              </div>
              <div>
                <label className={labelClass}>Role</label>
                <input type="text" value={p.role} onChange={update(p.slug, "role")} className={inputClass} data-testid={`talent-role-${p.slug}`} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Focus / Bio</label>
              <textarea rows={3} value={p.focus} onChange={update(p.slug, "focus")} className={inputClass} data-testid={`talent-focus-${p.slug}`} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Photo URL</label>
                <input type="url" value={p.img} onChange={update(p.slug, "img")} placeholder="https://…" className={inputClass} data-testid={`talent-img-${p.slug}`} />
              </div>
              <div>
                <label className={labelClass}>Photo Caption</label>
                <input type="text" value={p.caption} onChange={update(p.slug, "caption")} className={inputClass} data-testid={`talent-caption-${p.slug}`} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Areas of Expertise (one per line)</label>
              <textarea
                rows={4}
                value={(p.expertise || []).join("\n")}
                onChange={(e) => updateExpertise(p.slug, e.target.value)}
                className={inputClass}
                data-testid={`talent-expertise-${p.slug}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminTalentPage;
